import React, { useState } from 'react';
import './BFSGenerator.css';

const BFSGenerator = () => {
  // Configuration state
  const [availableDigits, setAvailableDigits] = useState([2]);
  const [operations, setOperations] = useState({
    add: true,
    subtract: true,
    multiply: true,
    divide: true
  });
  const [allowParentheses, setAllowParentheses] = useState(true);
  const [maxDigits, setMaxDigits] = useState(8);
  const [targetRange, setTargetRange] = useState({ min: 10, max: 50 });
  const [difficulty, setDifficulty] = useState('medium');

  // Generation state
  const [seed, setSeed] = useState(0);

  // Output state
  const [generatedPuzzle, setGeneratedPuzzle] = useState(null);
  const [status, setStatus] = useState('');
  const [showSolution, setShowSolution] = useState(false);

  // Deterministic RNG (Mulberry32)
  function mulberry32(a) {
    return function() {
      let t = a += 0x6D2B79F5;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  function seededInt(rng, min, max) {
    return Math.floor(rng() * (max - min + 1)) + min;
  }

  // Difficulty thresholds
  const DIFFICULTY_THRESHOLDS = {
    easy: { min: 10, max: 25 },
    medium: { min: 25, max: 45 },
    hard: { min: 45, max: 100 }
  };

  // Check if number is prime
  function isPrime(n) {
    if (n <= 1) return false;
    if (n <= 3) return true;
    if (n % 2 === 0 || n % 3 === 0) return false;
    for (let i = 5; i * i <= n; i += 6) {
      if (n % i === 0 || n % (i + 2) === 0) return false;
    }
    return true;
  }

  // BFS to find all reachable values
  function bfs(config, maxValue = 10000, maxQueueSize = 50000) {
    const { availableDigits, operations, allowParentheses, maxDigits } = config;

    // Map: value → { minDigits, expressions[], history }
    const reachable = new Map();
    const queue = [];

    // Initialize with available digits
    for (const digit of availableDigits) {
      reachable.set(digit, {
        minDigits: 1,
        expressions: [String(digit)],
        history: [digit]
      });
      queue.push({
        value: digit,
        expr: String(digit),
        digitsUsed: 1,
        history: [digit]
      });
    }

    let head = 0;
    while (head < queue.length && queue.length < maxQueueSize) {
      const current = queue[head++];

      if (current.digitsUsed >= maxDigits) continue;

      const nextStates = generateNextStates(current, config, reachable);

      for (const next of nextStates) {
        // Bounds checking
        if (Math.abs(next.value) > maxValue) continue;
        if (!Number.isFinite(next.value) || !Number.isInteger(next.value)) continue;

        const existing = reachable.get(next.value);

        if (!existing || next.digitsUsed < existing.minDigits) {
          // Found better path
          reachable.set(next.value, {
            minDigits: next.digitsUsed,
            expressions: [next.expr],
            history: next.history
          });
          queue.push(next);
        } else if (next.digitsUsed === existing.minDigits) {
          // Alternative equal-length path
          if (!existing.expressions.includes(next.expr)) {
            existing.expressions.push(next.expr);
          }
        }
      }
    }

    return reachable;
  }

  // Evaluate expression with standard operator precedence
  function evalExpr(expr) {
    try {
      return new Function(`return ${expr}`)();
    } catch (e) {
      return NaN;
    }
  }

  // Generate next states from current state
  function generateNextStates(current, config, reachable) {
    const states = [];
    const { availableDigits, operations, allowParentheses } = config;

    if (!allowParentheses) {
      // Mode 1: No parentheses - only append digits, evaluate with standard precedence
      for (const digit of availableDigits) {
        if (operations.add) {
          const newExpr = `${current.expr}+${digit}`;
          const newValue = evalExpr(newExpr);
          if (Number.isFinite(newValue) && Number.isInteger(newValue)) {
            states.push({
              value: newValue,
              expr: newExpr,
              digitsUsed: current.digitsUsed + 1,
              history: [...current.history, newValue]
            });
          }
        }

        if (operations.subtract) {
          const newExpr = `${current.expr}-${digit}`;
          const newValue = evalExpr(newExpr);
          if (Number.isFinite(newValue) && Number.isInteger(newValue)) {
            states.push({
              value: newValue,
              expr: newExpr,
              digitsUsed: current.digitsUsed + 1,
              history: [...current.history, newValue]
            });
          }
        }

        if (operations.multiply) {
          const newExpr = `${current.expr}*${digit}`;
          const newValue = evalExpr(newExpr);
          if (Number.isFinite(newValue) && Number.isInteger(newValue)) {
            states.push({
              value: newValue,
              expr: newExpr,
              digitsUsed: current.digitsUsed + 1,
              history: [...current.history, newValue]
            });
          }
        }

        if (operations.divide && digit !== 0) {
          const newExpr = `${current.expr}/${digit}`;
          const newValue = evalExpr(newExpr);
          if (Number.isFinite(newValue) && Number.isInteger(newValue)) {
            states.push({
              value: newValue,
              expr: newExpr,
              digitsUsed: current.digitsUsed + 1,
              history: [...current.history, newValue]
            });
          }
        }
      }
    }

    // Approach 2: Combine with previously reached values (if parentheses allowed)
    if (allowParentheses) {
      for (const [otherValue, otherData] of reachable.entries()) {
        // Skip combining with itself or values that would exceed digit limit
        if (current.digitsUsed + otherData.minDigits > config.maxDigits) continue;

        const otherExpr = otherData.expressions[0];

        if (operations.add) {
          states.push({
            value: current.value + otherValue,
            expr: `(${current.expr})+(${otherExpr})`,
            digitsUsed: current.digitsUsed + otherData.minDigits,
            history: [...current.history, current.value + otherValue]
          });
        }

        if (operations.subtract) {
          states.push({
            value: current.value - otherValue,
            expr: `(${current.expr})-(${otherExpr})`,
            digitsUsed: current.digitsUsed + otherData.minDigits,
            history: [...current.history, current.value - otherValue]
          });
        }

        if (operations.multiply) {
          states.push({
            value: current.value * otherValue,
            expr: `(${current.expr})*(${otherExpr})`,
            digitsUsed: current.digitsUsed + otherData.minDigits,
            history: [...current.history, current.value * otherValue]
          });
        }

        if (operations.divide && otherValue !== 0) {
          const result = current.value / otherValue;
          if (Number.isInteger(result)) {
            states.push({
              value: result,
              expr: `(${current.expr})/(${otherExpr})`,
              digitsUsed: current.digitsUsed + otherData.minDigits,
              history: [...current.history, result]
            });
          }
        }
      }
    }

    return states;
  }

  // Calculate difficulty score for a target
  function calculateDifficultyScore(target, solutionData, config) {
    let score = 0;
    const { expressions, minDigits, history } = solutionData;
    const solution = expressions[0];

    // 1. Creative division (d/d=1): +10 points
    for (const digit of config.availableDigits) {
      if (solution.includes(`${digit}/${digit}`)) {
        score += 10;
        break;
      }
    }

    // 2. Overshooting: any intermediate > target: +15 points
    const maxInHistory = Math.max(...history);
    if (maxInHistory > target) {
      score += 15;
    }

    // 3. Prime target: +8 points
    if (isPrime(target)) {
      score += 8;
    }

    // 4. Non-obvious intermediates: +5 per unique intermediate
    const uniqueIntermediates = new Set(history.filter(v => v !== target));
    score += uniqueIntermediates.size * 5;

    // 5. More operations: +2 per operation
    const operationCount = (solution.match(/[\+\-\*\/]/g) || []).length;
    score += operationCount * 2;

    // 6. Unique shortest solution: +20 points
    if (expressions.length === 1) {
      score += 20;
    }

    return score;
  }

  // Verify that an expression evaluates to the expected target
  function verifyExpression(expression, expectedValue) {
    try {
      const result = new Function(`return ${expression}`)();
      return result === expectedValue;
    } catch (e) {
      console.error(`Failed to evaluate expression: ${expression}`, e);
      return false;
    }
  }

  // Generate candidate puzzles from reachable values
  function generateCandidates(reachable, config) {
    const candidates = [];

    for (const [value, data] of reachable.entries()) {
      if (value < config.targetRange.min || value > config.targetRange.max) continue;
      if (data.minDigits < 3) continue; // Too trivial
      if (data.minDigits > config.maxDigits) continue; // Too long

      // Verify the solution is correct
      const solution = data.expressions[0];
      if (!verifyExpression(solution, value)) {
        console.error(`Invalid solution: ${solution} should equal ${value}`);
        continue; // Skip invalid solutions
      }

      const score = calculateDifficultyScore(value, data, config);

      // Calculate metadata for display
      const usesCreativeDivision = config.availableDigits.some(d =>
        solution.includes(`${d}/${d}`)
      );
      const overshoots = Math.max(...data.history) > value;
      const isPrimeTarget = isPrime(value);
      const uniqueIntermediates = new Set(data.history.filter(v => v !== value)).size;

      candidates.push({
        target: value,
        solution: solution,
        digitsUsed: data.minDigits,
        alternativeSolutions: data.expressions.length,
        score: score,
        metadata: {
          usesCreativeDivision,
          overshoots,
          isPrime: isPrimeTarget,
          uniqueIntermediates
        }
      });
    }

    return candidates;
  }

  // Select best puzzle from candidates based on difficulty
  function selectPuzzle(candidates, difficulty) {
    const threshold = DIFFICULTY_THRESHOLDS[difficulty];
    const suitable = candidates.filter(c =>
      c.score >= threshold.min && c.score <= threshold.max
    );

    if (suitable.length === 0) {
      // Fallback: pick closest to desired range
      return candidates.sort((a, b) => {
        const aDist = Math.min(
          Math.abs(a.score - threshold.min),
          Math.abs(a.score - threshold.max)
        );
        const bDist = Math.min(
          Math.abs(b.score - threshold.min),
          Math.abs(b.score - threshold.max)
        );
        return aDist - bDist;
      })[0];
    }

    // Pick highest scoring within range
    return suitable.sort((a, b) => b.score - a.score)[0];
  }

  // Validate configuration
  function validateConfig() {
    if (availableDigits.length === 0) {
      setStatus('❌ Please select at least one digit.');
      return false;
    }

    if (!operations.add && !operations.subtract && !operations.multiply && !operations.divide) {
      setStatus('❌ Please select at least one operation.');
      return false;
    }

    if (targetRange.min >= targetRange.max) {
      setStatus('❌ Invalid target range (min must be less than max).');
      return false;
    }

    if (maxDigits < 3 || maxDigits > 12) {
      setStatus('❌ Max digits must be between 3 and 12.');
      return false;
    }

    return true;
  }

  // Generate puzzle
  async function handleGenerate() {
    if (!validateConfig()) return;

    let actualSeed = seed;
    if (seed === 0) {
      actualSeed = Math.floor(Math.random() * (2 ** 31));
      setSeed(actualSeed);
    }

    setStatus('Generating puzzle...');
    setGeneratedPuzzle(null);

    const config = {
      availableDigits,
      operations,
      allowParentheses,
      maxDigits,
      targetRange
    };

    for (let attempt = 0; attempt < 100; attempt++) {
      const testSeed = (actualSeed + attempt) >>> 0;

      setStatus(`Searching for quality puzzle (attempt ${attempt + 1}/100)...`);

      // Run BFS
      const reachable = bfs(config);

      // Generate candidates
      const candidates = generateCandidates(reachable, config);

      if (candidates.length === 0) {
        continue; // Try next seed
      }

      // Select puzzle matching difficulty
      const puzzle = selectPuzzle(candidates, difficulty);

      if (puzzle && puzzle.score >= DIFFICULTY_THRESHOLDS[difficulty].min) {
        setSeed(testSeed);
        setGeneratedPuzzle(puzzle);
        setStatus(`✅ Generated puzzle with seed <span class="pill">${testSeed}</span>`);
        setShowSolution(false);
        return;
      }

      // Progress indicator
      if (attempt % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    setStatus('⚠️ Could not find ideal puzzle in 100 attempts. Try adjusting parameters.');
  }

  // Generate with new random seed
  async function handleNewSeed() {
    setSeed(0);
    await handleGenerate();
  }

  // Copy solution to clipboard
  async function handleCopySolution() {
    if (!generatedPuzzle) return;
    try {
      await navigator.clipboard.writeText(`${generatedPuzzle.solution} = ${generatedPuzzle.target}`);
      setStatus('✅ Solution copied to clipboard!');
    } catch (e) {
      console.error('Failed to copy:', e);
      setStatus('❌ Failed to copy to clipboard.');
    }
  }

  // Handle digit toggle
  function handleDigitToggle(digit, checked) {
    if (checked) {
      setAvailableDigits([...availableDigits, digit].sort());
    } else {
      setAvailableDigits(availableDigits.filter(d => d !== digit));
    }
  }

  // Handle operation toggle
  function handleOperationToggle(operation, checked) {
    setOperations({ ...operations, [operation]: checked });
  }

  // Get operations string for display
  function getOperationsString() {
    const ops = [];
    if (operations.add) ops.push('+');
    if (operations.subtract) ops.push('-');
    if (operations.multiply) ops.push('×');
    if (operations.divide) ops.push('÷');
    return ops.join(', ');
  }

  return (
    <div className="bfs-generator">
      <header>
        <h1>BFS Number Puzzle Generator</h1>
        <p className="subtitle">
          Find the shortest path to a target number using digits and operations
        </p>
      </header>

      <div className="controls">
        {/* Available Digits */}
        <div className="control-group">
          <label className="control-label">Available Digits</label>
          <div className="digit-selector">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
              <label key={d} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={availableDigits.includes(d)}
                  onChange={(e) => handleDigitToggle(d, e.target.checked)}
                />
                <span className="checkbox-text">{d}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Operations */}
        <div className="control-group">
          <label className="control-label">Operations</label>
          <div className="operations-selector">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={operations.add}
                onChange={(e) => handleOperationToggle('add', e.target.checked)}
              />
              <span className="checkbox-text">Addition (+)</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={operations.subtract}
                onChange={(e) => handleOperationToggle('subtract', e.target.checked)}
              />
              <span className="checkbox-text">Subtraction (-)</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={operations.multiply}
                onChange={(e) => handleOperationToggle('multiply', e.target.checked)}
              />
              <span className="checkbox-text">Multiplication (×)</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={operations.divide}
                onChange={(e) => handleOperationToggle('divide', e.target.checked)}
              />
              <span className="checkbox-text">Division (÷)</span>
            </label>
          </div>
        </div>

        {/* Parentheses Option */}
        <div className="control-group">
          <label className="checkbox-label parentheses-toggle">
            <input
              type="checkbox"
              checked={allowParentheses}
              onChange={(e) => setAllowParentheses(e.target.checked)}
            />
            <span className="checkbox-text">Allow Parentheses</span>
          </label>
          <p className="note">When enabled, can combine any two previously-reached values</p>
        </div>

        {/* Max Digits */}
        <div className="control-group">
          <label className="control-label">Max Digits</label>
          <input
            type="number"
            min="3"
            max="12"
            value={maxDigits}
            onChange={(e) => setMaxDigits(Number(e.target.value))}
            className="number-input"
          />
          <p className="note">Range: 3-12</p>
        </div>

        {/* Target Range */}
        <div className="control-group">
          <label className="control-label">Target Range</label>
          <div className="range-inputs">
            <input
              type="number"
              value={targetRange.min}
              onChange={(e) => setTargetRange({ ...targetRange, min: Number(e.target.value) })}
              placeholder="Min"
              className="number-input"
            />
            <span className="range-separator">to</span>
            <input
              type="number"
              value={targetRange.max}
              onChange={(e) => setTargetRange({ ...targetRange, max: Number(e.target.value) })}
              placeholder="Max"
              className="number-input"
            />
          </div>
        </div>

        {/* Difficulty */}
        <div className="control-group">
          <label className="control-label">Difficulty</label>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            className="select-input"
          >
            <option value="easy">Easy (10-25 score)</option>
            <option value="medium">Medium (25-45 score)</option>
            <option value="hard">Hard (45+ score)</option>
          </select>
        </div>

        {/* Seed */}
        <div className="control-group">
          <label className="control-label">Seed</label>
          <input
            type="number"
            value={seed}
            onChange={(e) => setSeed(Number(e.target.value))}
            className="number-input"
          />
          <p className="note">0 = random seed</p>
        </div>

        {/* Buttons */}
        <div className="buttons">
          <button className="btn-primary" onClick={handleGenerate}>
            Generate
          </button>
          <button className="btn-secondary" onClick={handleNewSeed}>
            New Seed & Generate
          </button>
          {generatedPuzzle && (
            <button className="btn-secondary" onClick={() => setShowSolution(!showSolution)}>
              {showSolution ? 'Hide' : 'Show'} Solution
            </button>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {status && (
        <div className="status" dangerouslySetInnerHTML={{ __html: status }}></div>
      )}

      {/* Puzzle Display */}
      {generatedPuzzle && (
        <div className="puzzle-display">
          <div className="puzzle-header">
            <h3>Generated Puzzle</h3>
            <div className="meta">
              <span className="pill">Target: {generatedPuzzle.target}</span>
              <span className="pill">Score: {generatedPuzzle.score}</span>
              <span className="pill">Seed: {seed}</span>
            </div>
          </div>

          <div className="puzzle-question">
            <p>
              Using the digit{availableDigits.length > 1 ? 's' : ''}{' '}
              <strong className="digit-list">{availableDigits.join(', ')}</strong> and
              operation{Object.values(operations).filter(Boolean).length > 1 ? 's' : ''}{' '}
              <strong className="ops-list">{getOperationsString()}</strong>, reach{' '}
              <strong className="target-number">{generatedPuzzle.target}</strong> using as few
              digits as possible.
            </p>
            {allowParentheses && (
              <p className="note">You may combine any previously reached values using parentheses.</p>
            )}
          </div>

          {showSolution && (
            <div className="solution">
              <h4>Solution ({generatedPuzzle.digitsUsed} digits):</h4>
              <div className="solution-expression">
                {generatedPuzzle.solution} = {generatedPuzzle.target}
              </div>

              {generatedPuzzle.alternativeSolutions > 1 && (
                <p className="note alternative-solutions">
                  Note: {generatedPuzzle.alternativeSolutions} equal-length solution{generatedPuzzle.alternativeSolutions > 1 ? 's' : ''} exist
                </p>
              )}

              <div className="difficulty-breakdown">
                <h5>Why this is {difficulty}:</h5>
                <ul>
                  {generatedPuzzle.metadata.usesCreativeDivision && (
                    <li>Uses creative division (e.g., 2÷2=1) to create small numbers</li>
                  )}
                  {generatedPuzzle.metadata.overshoots && (
                    <li>Solution overshoots the target, then subtracts back</li>
                  )}
                  {generatedPuzzle.metadata.isPrime && (
                    <li>Target is a prime number (fewer factor-based shortcuts)</li>
                  )}
                  {generatedPuzzle.metadata.uniqueIntermediates > 2 && (
                    <li>
                      Requires building {generatedPuzzle.metadata.uniqueIntermediates} intermediate values
                    </li>
                  )}
                  {generatedPuzzle.alternativeSolutions === 1 && (
                    <li>Unique shortest solution (only one optimal path)</li>
                  )}
                </ul>
              </div>

              <button className="btn-copy" onClick={handleCopySolution}>
                Copy Solution
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BFSGenerator;
